import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/SupportPage.css';
import API_BASE from '../utils/api.js';
import { fetchMySupportConversation } from '../utils/supportChat.js';
import SupportInbox from '../components/support/SupportInbox.js';
import SubscriberReport from '../components/support/SubscriberReport.js';
import AdminProductImport from '../components/support/productImport/AdminProductImport.js';
import { ARTICLES } from '../data/supportArticles.js';

const FAQS = [
  {
    question: 'What is InteriorFolio?',
    answer:
      'InteriorFolio is a project management workflow platform built specifically for interior designers. It helps streamline sourcing, product management, proposals, invoicing, procurement, time billing, and project organization all in one place.',
  },
  {
    question: 'Who is InteriorFolio designed for?',
    answer:
      'InteriorFolio is built for interior designers, design firms, decorators, and procurement-focused teams who want a cleaner, more organized way to manage projects and financial workflows.',
  },
  {
    question: 'Is InteriorFolio an Accounting software?',
    answer:
      'Not exactly. InteriorFolio is designed to work alongside your accounting system, while giving designers the project visibility and workflow tools traditional accounting software often lacks. The best part, it integrates directly within QuickBooks Online.',
    link: '/support/quickbooks-online',
    linkText: 'Learn how to connect QuickBooks',
  },
  {
    question: 'Can I track product sourcing and procurement?',
    answer:
      'Absolutely. You can organize vendors, products, specifications, pricing, procurement details, and client-facing information all within your Product Library.',
    link: '/support/products-services',
    linkText: 'View Products & Services guide',
  },
  {
    question: 'Can I create proposals and invoices?',
    answer:
      'Yes. InteriorFolio allows you to generate proposals, invoices, and project-related documents while keeping everything tied back to the project itself.',
    link: '/support/creating-proposals',
    linkText: 'View Creating Proposals guide',
  },
  {
    question: 'Does InteriorFolio support time billing?',
    answer:
      'Yes. You can track billable time, assign entries to projects, and manage service-based billing workflows directly within the platform.',
  },
  {
    question: 'Can I upload product images and tear sheets?',
    answer:
      'Yes. You can upload images, documents, specifications, and supporting files to keep your projects visually organized and client-ready.',
  },
  {
    question: 'Can multiple team members use the platform?',
    answer:
      'Yes. InteriorFolio supports multi-user access so your team can collaborate across projects and workflows. Visit the "Adding Team Members" section in Settings for more details on how to edit user rights.',
    link: '/support/managing-team',
    linkText: 'View Managing Your Team guide',
  },
  {
    question: 'Can I create purchase orders directly from Proposals and Invoices?',
    answer:
      'Yes. InteriorFolio allows you to convert approved proposals or invoices directly into purchase orders, helping streamline procurement workflows and reduce duplicate data entry.',
    link: '/support/creating-purchase-orders',
    linkText: 'View Creating Purchase Orders guide',
  },
  {
    question: 'How do I navigate the settings page?',
    answer:
      'You can access your Settings page by clicking your Profile Icon in the top right corner of the dashboard and selecting Settings from the dropdown menu. From there, you can manage your company information, team members, QuickBooks integration, tax locations, tags, and more.',
    link: '/support/managing-settings',
    linkText: 'View Managing Settings guide',
  },
  {
    question: 'How do I link InteriorFolio to QuickBooks Online?',
    answer:
      'Go to Settings and click the QuickBooks tab. Click "Connect to QuickBooks" and sign into your QuickBooks Online account, select your company file, and approve the integration permissions. After connecting, select your preferred Liability Account from the dropdown for client retainers and deposits.',
    link: '/support/quickbooks-online',
    linkText: 'View full QuickBooks setup guide',
  },
  {
    question: 'How do I create a proposal?',
    answer:
      'Navigate to your project and select the Proposals tab. Click "Add New," review your project information, then browse your product catalog to add items. You can customize trade price, selling price, quantity, markup, shipping, room assignments, and tags before saving.',
    link: '/support/creating-proposals',
    linkText: 'View full Proposals guide',
  },
  {
    question: 'How do I create an invoice?',
    answer:
      'You can create an invoice by converting an existing proposal (via the Actions menu → "Convert to Invoice") or by going to the Invoices tab in your project and clicking "Add New." InteriorFolio automatically pulls over all product, pricing, and client details when converting from a proposal.',
    link: '/support/creating-invoices',
    linkText: 'View full Invoices guide',
  },
  {
    question: 'How do I create a purchase order?',
    answer:
      'Open your project and select the Purchase Orders tab, then click "Add New." You can also convert a proposal or invoice into a purchase order via the Actions menu. Fill in vendor information, payment and delivery terms, add items from your catalog, and save or sync directly to QuickBooks as a bill.',
    link: '/support/creating-purchase-orders',
    linkText: 'View full Purchase Orders guide',
  },
  {
    question: 'Will there be additional features added over time?',
    answer:
      'Yes. InteriorFolio is actively evolving, and new features and improvements will continue to roll out as the platform grows. Have an idea? Contact us at info@interiorfolio.com.',
  },
  {
    question: 'How can I request a demo or assistance with using the software?',
    answer:
      'You can contact us directly through our website to learn more, request a walkthrough, or get additional support. You can also use the live chat button in the lower-left corner of the app.',
  },
];


export default function SupportPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [isSupportAdmin, setIsSupportAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminTab, setAdminTab] = useState('inbox');
  const [importOpened, setImportOpened] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    let active = true;

    async function loadViewer() {
      try {
        const data = await fetchMySupportConversation();
        if (!active) return;

        const viewerEmail = data?.viewer?.email || '';
        const rawViewerName = data?.viewer?.name || '';
        const viewerName = rawViewerName === viewerEmail ? '' : rawViewerName;
        setIsSupportAdmin(Boolean(data?.viewer?.isSupportAdmin));
        setForm((prev) => ({
          ...prev,
          name: prev.name || viewerName,
          email: prev.email || viewerEmail,
        }));
      } catch (err) {
        console.error('Failed to load support viewer context:', err);
      } finally {
        if (active) setCheckingAdmin(false);
      }
    }

    loadViewer();
    return () => { active = false; };
  }, []);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submitSupportRequest(e) {
    e.preventDefault();
    setStatus('loading');

    try {
      const res = await fetch(`${API_BASE}/api/notifications/support-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error('Support request failed');

      setStatus('sent');
      setForm((prev) => ({ ...prev, message: '' }));
    } catch (err) {
      console.error('Support request email failed:', err);
      setStatus('error');
    }
  }

  if (!checkingAdmin && isSupportAdmin) {
    return (
      <div className="support-page">
        <section className="support-header">
          <h1>Support Center</h1>
          <p>Admin tools for live customer chat, subscriber reporting and data imports.</p>
        </section>
        <div className="support-admin-tabs" role="tablist">
          {[
            ['inbox', 'Inbox'],
            ['subscribers', 'Subscribers'],
            ['import', 'Data Import'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={adminTab === key}
              className={`support-admin-tab${adminTab === key ? ' active' : ''}`}
              onClick={() => {
                setAdminTab(key);
                if (key === 'import') setImportOpened(true);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {adminTab === 'inbox' && <SupportInbox />}
        {adminTab === 'subscribers' && <SubscriberReport />}
        {/* Stays mounted once opened, so checking the inbox doesn't lose an import in progress. */}
        {importOpened && (
          <div hidden={adminTab !== 'import'}>
            <AdminProductImport />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="support-page">
      <section className="support-header">
        <h1>Support Center</h1>
        <p>Find answers, get help, and learn more about InteriorFolio.</p>
      </section>

      <div className="support-guides-section">
        <h2 className="support-guides-heading">Guides</h2>
        <div className="support-guides-grid">
          {ARTICLES.map((article) => (
            <Link key={article.slug} to={`/support/${article.slug}`} className="support-guide-card">
              <span className="support-guide-title">{article.title}</span>
              <span className="support-guide-arrow">→</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="support-content">
        <div className="support-card support-faq-card">
          <h2>Frequently Asked Questions</h2>
          <ul className="faq-list">
            {FAQS.map((faq, i) => (
              <li key={i} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && (
                  <div className="faq-answer-body">
                    <p className="faq-answer">{faq.answer}</p>
                    {faq.link && (
                      <Link to={faq.link} className="faq-learn-more">
                        {faq.linkText || 'Learn more'} →
                      </Link>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="support-card support-form-card">
          <h2>Send a Message</h2>
          <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '12px' }}>
            We&apos;ll reply to your email within one business day.
          </p>
          <form onSubmit={submitSupportRequest} className="support-form">
            <input
              type="text"
              placeholder="Your Name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
            />
            <input
              type="email"
              placeholder="Your Email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
            />
            <textarea
              placeholder="How can we help?"
              value={form.message}
              onChange={(e) => updateField('message', e.target.value)}
              rows={4}
              required
            />
            <button type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? 'Sending...' : 'Send Message'}
            </button>
            {status === 'sent' && (
              <p className="support-form-status success">
                Message sent! We&apos;ll reply to your email shortly.
              </p>
            )}
            {status === 'error' && (
              <p className="support-form-status error">
                Could not send your message. Please try again or email us directly at{' '}
                <a href="mailto:info@interiorfolio.com">info@interiorfolio.com</a>.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
